import isString from 'lodash/isString';
import memoize from 'lodash/memoize';

import * as Constants from '../constants';
import Logger from '../tools/Logger';

import { isAPSPandoraStationEnabled } from './ListenerUtils';

const log = Logger.child({ component: 'SourceTypeUtil' });

export function getPandoraTypeFromPandoraId(pandoraId) {
  if (!pandoraId) {
    return null;
  }
  const maybePandoraType = pandoraId && pandoraId.split(':')[0];
  const isPandoraType = Object.values(Constants.SourceTypes.PandoraTypes).includes(
    maybePandoraType,
  );
  if (!isPandoraType) {
    log.info(`getPandoraTypeFromPandoraId failed to find type for ${pandoraId}`);
  }
  return isPandoraType ? maybePandoraType : null;
}

export function getSourceTypeFromPandoraId(pandoraId) {
  const pandoraType = getPandoraTypeFromPandoraId(pandoraId);
  const source = pandoraType && Constants.SourceTypes.PandoraTypeSources[pandoraType];
  const sourceType = source && Constants.SourceTypes.SourceTypes[source];
  if (!sourceType) {
    log.info(`getSourceTypeFromPandoraId failed to find type for ${pandoraId}`);
  }
  return sourceType;
}

export function getDesktopBridgeSourceType(sourceType) {
  return !sourceType ||
    [
      Constants.SourceTypes.SourceTypes.AUTOPLAY,
      Constants.SourceTypes.SourceTypes.STATION,
    ].includes(sourceType)
    ? 'station'
    : 'ondemand';
}

export function isPseudoSource(type) {
  return Constants.SourceTypes.PseudoSourceTypes.indexOf(type) !== -1;
}

export function getPseudoSourceTypeFromUrlParams(parentType, pseudoType) {
  if (pseudoType === Constants.Catalog.SourceTypeUrlParams.TOP_SONGS) {
    return Constants.SourceTypes.SourceTypes.ARTIST_TOP_SONGS;
  } else if (pseudoType === Constants.Catalog.SourceTypeUrlParams.ALL_SONGS) {
    if (parentType === Constants.Catalog.SourceTypeUrlParams.ARTIST) {
      return Constants.SourceTypes.SourceTypes.ARTIST_ALL_SONGS;
    }
    return Constants.SourceTypes.SourceTypes.COLLECTED_TRACKS;
  } else if (pseudoType === Constants.Catalog.SourceTypeUrlParams.THUMBED_UP) {
    return Constants.SourceTypes.SourceTypes.STATION_THUMB_UP;
  }
  return null;
}

export function getCollectedTrackPseudoId(listenerId) {
  return `${Constants.SourceTypes.PandoraTypes.COLLECTED_TRACKS}:${listenerId}`;
}

export function getStationFeedbackPseudoId(stationId) {
  return `${Constants.SourceTypes.PandoraTypes.STATION_THUMB_UP}:${stationId}`;
}

export function getCollectedAlbumTracksId(albumId) {
  return `${Constants.SourceTypes.PandoraTypes.COLLECTED_ALBUM_TRACKS}:${
    Constants.SourceTypes.PseudoObfuscations[Constants.SourceTypes.PandoraTypes.ALBUM]
  }:${albumId.split(':')[1]}`;
}

export function getArtistStationFactoryId(artistId) {
  return `${Constants.SourceTypes.PandoraTypes.STATION}:${
    Constants.SourceTypes.PseudoObfuscations[Constants.SourceTypes.PandoraTypes.ARTIST]
  }:${artistId.slice(1)}`;
}

export function getArtistAllSongsId(artistId) {
  const artistPrefix = getPandoraTypeFromPandoraId(artistId);
  return `${Constants.SourceTypes.PandoraTypes.ARTIST_ALL_SONGS}:${
    Constants.SourceTypes.PseudoObfuscations[artistPrefix]
  }:${artistId.split(':')[1]}`;
}

export function getArtistTopSongsId(artistId) {
  const artistPrefix = getPandoraTypeFromPandoraId(artistId);
  return `${Constants.SourceTypes.PandoraTypes.ARTIST_TOP_SONGS}:${
    Constants.SourceTypes.PseudoObfuscations[artistPrefix]
  }:${artistId.split(':')[1]}`;
}

export function isStationType(type) {
  return Constants.SourceTypes.StationTypes.concat([
    Constants.SourceTypes.PandoraTypes.STATION,
    Constants.SourceTypes.PandoraTypes.GENRE,
    Constants.SourceTypes.PandoraTypes.HYBRID_STATION,
  ]).includes(type);
}

export function isPandoraTypeAPSEnabled(state, pandoraType) {
  const isAPSStationEnabled = isAPSPandoraStationEnabled(state);
  return !isStationType(pandoraType) || isAPSStationEnabled;
}

export function isPlaylistPandoraId(pandoraId) {
  if (isString(pandoraId)) {
    return pandoraId.startsWith(Constants.SourceTypes.PandoraTypes.PLAYLIST);
  }
  return false;
}

export function isPodcastEpisodePandoraId(pandoraId) {
  if (isString(pandoraId)) {
    return pandoraId.startsWith(Constants.SourceTypes.PandoraTypes.EPISODE);
  }
  return false;
}

export function isPodcastPandoraType(type) {
  return (
    type === Constants.SourceTypes.PandoraTypes.PODCAST ||
    type === Constants.SourceTypes.PandoraTypes.EPISODE
  );
}

function getFilterTypes(shouldShowAllFilterTypes, isSearchFilter) {
  let filterTypes = [];
  if (shouldShowAllFilterTypes) {
    filterTypes = [...Constants.FilterNav.AllFilterTypes];
  } else {
    filterTypes = [...Constants.FilterNav.DefaultFilterTypes];
  }
  if (!isSearchFilter) {
    filterTypes.push(Constants.FilterNav.FilterNavTypes.EPISODES);
  }
  return filterTypes;
}

export const getMemoizedFilterTypes = memoize(getFilterTypes, (...args) => JSON.stringify(args));
